import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import Modal, { TextArea, Select, PrimaryButton, SecondaryButton, GhostButton } from "@/components/FormKit";
import { toast } from "sonner";
import { fmtDateTime, relativeTime } from "@/lib/format";

export default function Tickets() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const load = async () => setItems((await api.get("/company/tickets")).data);
  useEffect(() => { load(); }, []);

  const openTicket = async (id) => {
    const { data } = await api.get(`/company/tickets/${id}`);
    setActive(data);
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    try {
      await api.post(`/company/tickets/${active.id}/responses`, { body: reply });
      toast.success("Reply sent");
      setReply("");
      openTicket(active.id);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const changeStatus = async (status) => {
    try {
      await api.patch(`/company/tickets/${active.id}/status`, { status });
      toast.success(`Status: ${status}`);
      openTicket(active.id);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="company-tickets-page">
      <PageHeader eyebrow="Support" title="Customer tickets" description="Reply to customer questions and track resolution." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="grid-surface divide-y divide-stone-100 max-h-[70vh] overflow-y-auto" data-testid="tickets-list">
          {items.length === 0 && (
            <div className="p-8 text-center overline text-stone-400">No tickets yet</div>
          )}
          {items.map((t) => (
            <button
              key={t.id}
              onClick={() => openTicket(t.id)}
              data-testid={`ticket-item-${t.id}`}
              className={`w-full text-left p-4 hover:bg-stone-50 transition-colors border-l-4 ${
                active?.id === t.id ? "border-l-brand-500 bg-stone-50" : "border-l-transparent"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-display font-bold text-sm text-stone-950 flex-1">{t.subject}</div>
                <StatusBadge value={t.status} />
              </div>
              <div className="text-xs text-stone-500 mt-1 font-mono">
                {t.customer?.name || "—"} · {relativeTime(t.created_at)}
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {active ? (
            <div className="grid-surface p-6" data-testid="active-ticket">
              <div className="flex items-start justify-between gap-4 pb-4 border-b border-stone-200">
                <div>
                  <div className="overline text-stone-500">
                    {active.customer?.name} · {fmtDateTime(active.created_at)}
                  </div>
                  <h3 className="font-display text-2xl font-bold mt-1">{active.subject}</h3>
                  <p className="text-stone-700 mt-3">{active.body}</p>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <StatusBadge value={active.status} />
                  <div className="flex flex-col gap-1">
                    {active.status !== "in_progress" && <GhostButton onClick={() => changeStatus("in_progress")}>Mark In Progress</GhostButton>}
                    {active.status !== "resolved" && <GhostButton onClick={() => changeStatus("resolved")}>Resolve</GhostButton>}
                    {active.status !== "closed" && <GhostButton onClick={() => changeStatus("closed")}>Close</GhostButton>}
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {(active.responses || []).map((r) => (
                  <div
                    key={r.id}
                    className={`p-3 border ${r.author_role === "company" ? "border-brand-500 bg-brand-50" : "border-stone-200 bg-white"}`}
                  >
                    <div className="text-xs uppercase tracking-wider font-bold text-stone-500">
                      {r.author_role === "company" ? "YOU" : "CUSTOMER"} · {r.author_name}
                    </div>
                    <div className="text-sm text-stone-800 mt-1">{r.body}</div>
                    <div className="text-[10px] font-mono text-stone-400 mt-1">{fmtDateTime(r.created_at)}</div>
                  </div>
                ))}
              </div>

              <form onSubmit={sendReply} className="mt-4 border-t border-stone-200 pt-4">
                <TextArea testId="ticket-reply-input" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" />
                <div className="flex justify-end mt-2">
                  <PrimaryButton testId="send-reply-button" type="submit">Send reply</PrimaryButton>
                </div>
              </form>
            </div>
          ) : (
            <div className="grid-surface border-dashed p-10 text-center overline text-stone-400">
              Select a ticket to view
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
